
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPhoneLink } from "@/utils/formatters";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useCallSession } from "@/hooks/useCallSession";
import { useEffect, useState } from "react";

interface CallButtonProps {
  // Text label shown on the button (e.g. "Call")
  name: string;
  // Actual callee name to show in the "Forwarding your call to ..." UI
  displayName?: string;
  phone?: string;
  loanApplicationId?: number | string;
  loanId?: number;
  repaymentId?: number | string;
  contactType?: number;
  variant?: "default" | "ghost" | "outline";
  size?: "sm" | "default";
  className?: string;
  /**
   * When true:
   *   - Behaves like the original implementation: directly opens the phone dialer via tel: link.
   *   - No TeleCMI initiate/polling/comment flow.
   *
   * When false/undefined:
   *   - Uses the TeleCMI flow: directly shows forwarding call UI and call summary.
   *   - Skips Pre/Post EMI selection dialog, goes straight to call initiation.
   */
  useDialer?: boolean;
  /**
   * Callback function called after comment is saved successfully
   */
  onCommentSaved?: () => void;
}

const CallButton = ({
  name,
  displayName,
  phone,
  loanApplicationId,
  loanId,
  repaymentId,
  contactType,
  variant = "ghost",
  size = "sm",
  className = "",
  useDialer: propUseDialer,
  onCommentSaved,
}: CallButtonProps) => {
  const { user } = useAuth();
  const { loading, startTeleCMICall, phase } = useCallSession();
  const [isThisCalling, setIsThisCalling] = useState(false);

  useEffect(() => {
    if (phase === "idle") {
      setIsThisCalling(false);
    }
  }, [phase]);

  // Determine useDialer based on feature_access_ids
  // If feature_access_ids includes 1, useDialer = false (use TeleCMI flow)
  // Otherwise, useDialer = true (use direct dialer)
  const hasFeatureAccessId1 = user?.feature_access_ids?.includes(1) ?? false;
  const useDialer = propUseDialer !== undefined 
    ? propUseDialer 
    : !hasFeatureAccessId1; // Default: if has ID 1, use TeleCMI (useDialer=false), else use dialer (useDialer=true)

  if (!phone) return null;

  const phoneLink = formatPhoneLink(phone);
  if (!phoneLink) return null;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up to parent elements

    // When useDialer is true, use the original direct dialer behavior.
    // This keeps API logic on the button click (if you add it here) and
    // opens the device dialer via tel: link.
    if (useDialer) {
      window.location.href = phoneLink;
      return;
    }

    // If we have TeleCMI context, directly initiate call (skip Pre/Post EMI dialog)
    if (loanApplicationId != null && repaymentId != null && contactType != null) {
      setIsThisCalling(true);
      await startTeleCMICall({
        name,
        displayName,
        loanApplicationId: Number(loanApplicationId),
        loanId,
        repaymentId: Number(repaymentId),
        contactType: Number(contactType),
        onCommentSaved,
      });
      return;
    }

    // No TeleCMI data – show popup instead of calling
    toast({
      variant: "destructive",
      title: "Call not available",
      description: "For this user we don't have calling data configured.",
    });

    // If you still want to open phone dialer after the message, uncomment:
    // window.location.href = phoneLink;
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={`gap-1 text-black hover:text-gray-800 hover:bg-gray-50 ${className}`}
      onClick={handleClick}
      disabled={loading}
    >
      <Phone className="h-3 w-3 text-black" />
      <span className="text-xs text-black">
        {isThisCalling ? "Calling..." : name}
      </span>
    </Button>
  );
};

export default CallButton;
